import { useCallback, useEffect, useState } from 'preact/hooks'
import { createPortal } from 'preact/compat'
import { useAthosContext } from '../fetchers/provider'
import { useBeaconLogImpressions, useBeaconLogRender } from '../hooks/useBeaconAnalytics'
import { ResultsCard } from './ResultCard'
import { SkeletonCard } from './SkeletonCard'
import Icon from '@/components/snippets/icon/icon'
import { dataTracker } from '@/components/snippets/analytics/analytics'

export function CarouselControl({ role, icon }) {
  return (
    <button data-role={role} class="btn btn-icon btn-md btn-inline btn-outline-black justify-center">
      <Icon id={icon} className="w-5 h-5" />
    </button>
  )
}

// Fills the widest layout (md:basis-1/6) so the row never renders short while loading
const SKELETON_CELLS = 6

export function CarouselResults({ className, hideControlsOnMobile, controlClass, toggleControls, maxResults, beaconEnabled = true, imageSizes }) {
  const { results, isLoading } = useAthosContext()
  const [node, setNode] = useState()
  const displayFlickityControllers = !(toggleControls === 'true')

  // A tabbed panel only fetches when it is first opened, so hold the row's height with
  // skeletons until the profile responds instead of collapsing to the progress bar.
  // Gated on isLoading as well so a failed request stops pulsing rather than never resolving
  const hasResponse = Array.isArray(results?.results)
  const showSkeleton = !hasResponse && isLoading

  // Limit results to the specified maximum if provided
  const displayResults = maxResults && results?.results ? results.results.slice(0, maxResults) : results?.results

  const SliderPortal = useCallback(
    ({ children }) => {
      if (!node) return children
      const target = node.querySelector('.flickity-slider')
      if (!target) return children
      return createPortal(children, target)
    },
    [node]
  )

  useEffect(() => {
    if (!node || !displayResults?.length) return

    const frame = requestAnimationFrame(() => {
      if (typeof node.syncFlickity === 'function') {
        node.syncFlickity()
        return
      }
      node.flickity?.reloadCells()
      node.flickity?.resize()
    })

    return () => cancelAnimationFrame(frame)
  }, [node, results, displayResults?.length])

  // Analytics related hooks
  useBeaconLogRender({ enabled: beaconEnabled })
  useBeaconLogImpressions(node, { enabled: beaconEnabled })

  if (hasResponse && displayResults.length === 0) return null
  if (!hasResponse && !showSkeleton) return null

  return (
    <x-flickity ref={setNode}>
      {displayFlickityControllers && (
        <div
          data-role="controller"
          class={`${hideControlsOnMobile ? 'hidden md:flex' : 'flex'} flex-row justify-end gap-2 pb-10 ${
            controlClass || ''
          }`}
        >
          <CarouselControl role="previous" icon="chevron-left" />
          <CarouselControl role="next" icon="chevron-right" />
        </div>
      )}
      {/* display results */}
      <div data-role="container" class="md:flex [&.flickity-enabled]:block">
        <SliderPortal>
          {showSkeleton &&
            Array.from({ length: SKELETON_CELLS }).map((_, i) => (
              <SkeletonCard key={`skeleton-${i}`} className={className} aspect="aspect-1" />
            ))}
          {displayResults?.map((result, index) => (
            <div key={result.handle || result.uid || index} className={`${className} flex flex-col shrink-0 min-h-full`}>
              <ResultsCard
                wishlist={{
                  type: 'none'
                }}
                result={result}
                index={index}
                className="wk-wishlist-button-target"
                imageSizes={imageSizes}
              />
            </div>
          ))}
        </SliderPortal>
      </div>
      {displayFlickityControllers && (
        <div data-role="progress-bar" class="mt-8 h-1 w-full bg-neutrals-light-grey">
          <div class="h-full w-[--progress] bg-primary-black transition-all duration-200"></div>
        </div>
      )}
    </x-flickity>
  )
}
